import React, { useState } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../../../components/ui/table';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../../../components/ui/dropdown-menu';
import { Badge } from '../../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription, DialogClose
} from '../../../components/ui/dialog';
import { 
  Tabs, TabsContent, TabsList, TabsTrigger 
} from '../../../components/ui/tabs';
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from '../../../components/ui/card';
import { 
  ChevronDown,
  Edit,
  Eye,
  FileText,
  Filter,
  Plus,
  Search,
  Trash2,
  X as XIcon,
  AlertCircle,
  Clock,
  EyeOff,
  Check as CheckIcon,
  Copy
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '../../../lib/utils';
import { useToast } from '../../../components/ui/use-toast';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAdminArtworks, fetchAdminArtCategories, deleteArtwork, updateArtwork } from '../../../services/adminPortfolioService';
import { ArtWork, ArtCategory } from '../../../services/portfolioService';
import { supabase } from '../../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { useDomainQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';

export const ArtworkList = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'published' | 'draft'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [artworkToDelete, setArtworkToDelete] = useState<ArtWork | null>(null);
  const [deletingArtwork, setDeletingArtwork] = useState(false);
  const [editingTitle, setEditingTitle] = useState<{artworkId: string | null, value: string}>({ artworkId: null, value: '' });
  const [editingYear, setEditingYear] = useState<{artworkId: string | null, value: string}>({ artworkId: null, value: '' });
  const [editingCategories, setEditingCategories] = useState<{artworkId: string | null}>({ artworkId: null });
  const [editingStatus, setEditingStatus] = useState<{artworkId: string | null, value?: 'draft' | 'published'}>({ artworkId: null });
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Record<string, string[]>>({});
  
  // Utiliser la logique de domaine
  const { getFilterParams } = useDomainQuery();
  const { domain } = getFilterParams();
  
  // Récupérer les catégories
  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['adminArtCategories', domain],
    queryFn: () => fetchAdminArtCategories(domain ? domain : '')
  });

  // Récupérer les œuvres
  const { data: artworks, error, isLoading, refetch } = useQuery({
    queryKey: ['adminArtworks', searchQuery, viewMode, selectedCategory],
    queryFn: () => fetchAdminArtworks(viewMode, searchQuery, selectedCategory),
  });

  // Initialiser les catégories sélectionnées quand les œuvres sont chargées
  React.useEffect(() => {
    if (artworks) {
      // Initialiser les catégories sélectionnées pour chaque œuvre
      const initialSelectedCategories = artworks.reduce((acc: Record<string, string[]>, artwork: ArtWork) => {
        acc[artwork.id] = artwork.categories?.map(cat => cat.id) || [];
        return acc;
      }, {});
      setSelectedCategoryIds(prev => ({
        ...prev,
        ...initialSelectedCategories
      }));
    }
  }, [artworks]);

  // Gestion de la suppression
  const handleDeleteClick = (artwork: ArtWork) => {
    setArtworkToDelete(artwork);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!artworkToDelete) return;
    
    setDeletingArtwork(true);
    try {
      await deleteArtwork(artworkToDelete.id);
      
      toast({
        title: 'Projet supprimé',
        description: 'Le projet a été supprimé avec succès',
      });
      
      // Rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['adminArtworks'] });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression du projet.',
        variant: 'destructive',
      });
    } finally {
      setDeletingArtwork(false);
      setDeleteDialogOpen(false);
      setArtworkToDelete(null);
    }
  };

  // Mise à jour du titre
  const updateArtworkTitle = async (artworkId: string, newTitle: string) => {
    if (!newTitle.trim()) return;
    
    try {
      const { tenantId, domain } = getFilterParams();
      await updateArtwork(artworkId, { title: newTitle }, tenantId, domain || undefined);
      
      toast({
        title: 'Titre mis à jour',
        description: 'Le titre du projet a été modifié avec succès',
      });
      
      // Rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['adminArtworks'] });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du titre:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le titre du projet',
        variant: 'destructive',
      });
    } finally {
      setEditingTitle({ artworkId: null, value: '' });
    }
  };

  // Mise à jour de l'année
  const updateArtworkYear = async (artworkId: string, newYear: string) => {
    try {
      const year = newYear ? parseInt(newYear) : null;
      const { tenantId, domain } = getFilterParams();
      await updateArtwork(artworkId, { year: year?.toString() || undefined }, tenantId, domain || undefined);
      
      toast({
        title: 'Année mise à jour',
        description: 'L\'année a été modifiée avec succès',
      });
      
      // Rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['adminArtworks'] });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'année:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour l\'année',
        variant: 'destructive',
      });
    } finally {
      setEditingYear({ artworkId: null, value: '' });
    }
  };

  // Mise à jour du statut
  const updateArtworkStatus = async (artworkId: string, newStatus: 'draft' | 'published') => {
    try {
      const { tenantId, domain } = getFilterParams();
      await updateArtwork(artworkId, { status: newStatus }, tenantId, domain || undefined);
      
      toast({
        title: 'Statut mis à jour',
        description: `Le projet est maintenant ${newStatus === 'published' ? 'publié' : 'en brouillon'}`,
      });
      
      // Rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['adminArtworks'] });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le statut',
        variant: 'destructive',
      });
    } finally {
      setEditingStatus({ artworkId: null });
    }
  };

  // Initialiser les catégories sélectionnées pour une œuvre
  const initializeSelectedCategories = (artwork: ArtWork) => {
    setSelectedCategoryIds(prev => ({
      ...prev,
      [artwork.id]: artwork.categories?.map(cat => cat.id) || []
    }));
  };

  // Mise à jour des catégories
  const updateArtworkCategories = async (artworkId: string) => {
    try {
      // Supprimer toutes les catégories existantes pour cette œuvre
      const { error: deleteError } = await supabase
        .from('portfolio_artwork_categories')
        .delete()
        .eq('artwork_id', artworkId);

      if (deleteError) throw deleteError;

      // Ajouter les nouvelles catégories
      const categoryIds = selectedCategoryIds[artworkId] || [];
      if (categoryIds.length > 0) {
        const { error: insertError } = await supabase
          .from('portfolio_artwork_categories')
          .insert(categoryIds.map(categoryId => ({
            artwork_id: artworkId,
            category_id: categoryId
          })));

        if (insertError) throw insertError;
      }
      
      toast({
        title: 'Catégories mises à jour',
        description: 'Les catégories du projet ont été modifiées avec succès',
      });
      
      // Rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['adminArtworks'] });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des catégories:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les catégories du projet',
        variant: 'destructive',
      });
    } finally {
      setEditingCategories({ artworkId: null });
    }
  };

  // Gestion du changement de catégorie
  const handleCategoryChange = (artworkId: string, categoryId: string, isChecked: boolean) => {
    setSelectedCategoryIds(prev => {
      const currentCategories = [...(prev[artworkId] || [])];
      const updatedCategories = isChecked
        ? [...currentCategories, categoryId]
        : currentCategories.filter(id => id !== categoryId);
      
      return {
        ...prev,
        [artworkId]: updatedCategories
      };
    });
  };

  // Fonction pour dupliquer une œuvre
  const handleDuplicate = async (id: string) => {
    try {
      // Récupérer l'œuvre originale
      const { data: original, error } = await supabase
        .from('portfolio_artworks')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!original) return;

      // Créer une copie avec nouvel ID et nouveau slug
      const duplicate = { 
        ...original, 
        id: uuidv4(),
        slug: `${original.slug}-copie-${Date.now()}`,
        created_at: new Date().toISOString()
      };

      // Supprimer la relation avec les catégories (sera recréée plus bas)
      delete duplicate.categories;

      // Insérer la copie dans la base de données
      const { error: insertError } = await supabase
        .from('portfolio_artworks')
        .insert([duplicate]);

      if (insertError) throw insertError;

      // Dupliquer les relations de catégories
      if (original.categories && original.categories.length > 0) {
        const categoriesData = original.categories.map((cat: any) => ({
          artwork_id: duplicate.id,
          category_id: cat.id
        }));

        const { error: categoriesError } = await supabase
          .from('portfolio_artwork_categories')
          .insert(categoriesData);

        if (categoriesError) throw categoriesError;
      }

      toast({
        title: 'Succès',
        description: 'Œuvre dupliquée avec succès',
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de dupliquer l\'œuvre',
        variant: 'destructive',
      });
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">Gestion des Projets</h1>
          <Button asChild variant="default" className="bg-yellow-500 hover:bg-yellow-600 text-black">
            <Link to="/admin/portfolio/create">
              <Plus className="mr-2 h-4 w-4" /> Nouveau projet
            </Link>
          </Button>
        </div>

        {/* Filtres */}
        <div className="mb-8 bg-black/20 backdrop-blur-sm rounded-xl border border-white/10 p-6 shadow-lg">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-300 mb-1">Rechercher</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Rechercher un projet..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                />
              </div>
            </div>
            
            <div className="w-full md:w-48">
              <label className="block text-sm font-medium text-gray-300 mb-1">Statut</label>
              <div className="relative">
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as 'all' | 'published' | 'draft')}
                  className="w-full appearance-none rounded-md bg-white/5 border border-white/10 text-white py-2 pl-3 pr-8 focus:border-yellow-500/50 focus:ring-yellow-500/50 cursor-pointer"
                >
                  <option value="all" className="bg-gray-800 text-white">Tous</option>
                  <option value="published" className="bg-gray-800 text-white">Publiés</option>
                  <option value="draft" className="bg-gray-800 text-white">Brouillons</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
            
            <div className="w-full md:w-48">
              <label className="block text-sm font-medium text-gray-300 mb-1">Catégorie</label>
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full appearance-none rounded-md bg-white/5 border border-white/10 text-white py-2 pl-3 pr-8 focus:border-yellow-500/50 focus:ring-yellow-500/50 cursor-pointer"
                >
                  <option value="" className="bg-gray-800 text-white">Toutes</option>
                  {categories?.map((category: ArtCategory) => (
                    <option key={category.id} value={category.id} className="bg-gray-800 text-white">
                      {category.name} ({category.artwork_count || 0})
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
            
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setViewMode('all');
                setSelectedCategory('');
              }}
              className="border-white/10 text-white hover:bg-white/5"
            >
              <XIcon className="mr-2 h-4 w-4" /> Réinitialiser
            </Button>
          </div>
        </div>

        {/* Liste des œuvres */}
        <div className="bg-black/20 backdrop-blur-sm rounded-xl border border-white/10 p-6 shadow-lg">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Clock className="animate-spin h-8 w-8 text-yellow-500 mr-3" />
              <span className="text-white text-lg">Chargement...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20">
              <AlertCircle className="h-12 w-12 text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">Erreur de chargement</h3>
              <p className="text-gray-400">Impossible de récupérer les projets</p>
              <Button onClick={() => refetch()} className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black">
                Réessayer
              </Button>
            </div>
          ) : artworks && artworks.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Titre</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Catégories</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Année</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Statut</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Date</th>
                    <th className="py-3 px-4 text-right text-sm font-medium text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {artworks.map((artwork: ArtWork, index) => (
                    <tr key={artwork.id} className={`border-b border-white/5 hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          {artwork.featured_image_url && (
                            <div className="w-10 h-10 rounded overflow-hidden mr-3 flex-shrink-0">
                              <img 
                                src={artwork.featured_image_url} 
                                alt={artwork.title} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="min-w-0">
                            {editingTitle.artworkId === artwork.id ? (
                              <div className="relative">
                                <Input
                                  type="text"
                                  value={editingTitle.value}
                                  onChange={(e) => setEditingTitle({ artworkId: artwork.id, value: e.target.value })}
                                  onBlur={() => updateArtworkTitle(artwork.id, editingTitle.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateArtworkTitle(artwork.id, editingTitle.value);
                                    } else if (e.key === 'Escape') {
                                      setEditingTitle({ artworkId: null, value: '' });
                                    }
                                  }}
                                  className="w-full bg-white/5 border-white/10 text-white h-8 px-2 text-sm"
                                  autoFocus
                                />
                              </div>
                            ) : (
                              <div 
                                className="font-medium text-white hover:bg-white/5 px-2 py-1 rounded cursor-pointer"
                                onClick={() => setEditingTitle({ artworkId: artwork.id, value: artwork.title })}
                              >
                                {artwork.title}
                              </div>
                            )}
                            <div className="text-sm text-gray-400 truncate max-w-xs">{artwork.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 relative">
                        {editingCategories.artworkId === artwork.id ? (
                          <div className="absolute z-50 bg-gray-900 border border-white/10 rounded-md shadow-lg p-2 w-64">
                            <div className="max-h-60 overflow-y-auto">
                              {categories?.map((category: ArtCategory) => (
                                <label key={category.id} className="flex items-center space-x-2 p-1 hover:bg-white/5 rounded">
                                  <input
                                    type="checkbox"
                                    checked={selectedCategoryIds[artwork.id]?.includes(category.id) || false}
                                    onChange={(e) => {
                                      handleCategoryChange(artwork.id, category.id, e.target.checked);
                                    }}
                                    className="rounded border-white/20 text-yellow-500 focus:ring-yellow-500"
                                  />
                                  <span className="text-sm text-white">{category.name}</span>
                                </label>
                              ))}
                            </div>
                            <div className="flex justify-end mt-2 pt-2 border-t border-white/10">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-xs h-7 text-white hover:text-white/80"
                                onClick={() => setEditingCategories({ artworkId: null })}
                              >
                                Annuler
                              </Button>
                              <Button 
                                variant="default" 
                                size="sm" 
                                className="text-xs h-7 ml-2 bg-yellow-500 hover:bg-yellow-600 text-black"
                                onClick={() => updateArtworkCategories(artwork.id)}
                              >
                                Enregistrer
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="flex flex-wrap gap-1 cursor-pointer hover:bg-white/5 p-1 rounded"
                            onClick={() => {
                              initializeSelectedCategories(artwork);
                              setEditingCategories({ artworkId: artwork.id });
                            }}
                          >
                            {artwork.categories?.slice(0, 2).map((category) => (
                              <Badge key={category.id} variant="outline" className="border-yellow-500/30 text-yellow-500">
                                {category.name}
                              </Badge>
                            ))}
                            {artwork.categories && artwork.categories.length > 2 && (
                              <Badge variant="outline" className="border-gray-500/30 text-gray-400">
                                +{artwork.categories.length - 2}
                              </Badge>
                            )}
                            {(!artwork.categories || artwork.categories.length === 0) && (
                              <span className="text-xs text-gray-400 italic">Aucune catégorie</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingYear.artworkId === artwork.id ? (
                          <div className="relative">
                            <Input
                              type="text"
                              value={editingYear.value}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                setEditingYear({ artworkId: artwork.id, value });
                              }}
                              onBlur={() => updateArtworkYear(artwork.id, editingYear.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateArtworkYear(artwork.id, editingYear.value);
                                } else if (e.key === 'Escape') {
                                  setEditingYear({ artworkId: null, value: '' });
                                }
                              }}
                              className="w-20 bg-white/5 border-white/10 text-white h-8 px-2 text-sm"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div 
                            className="text-gray-300 hover:bg-white/5 px-2 py-1 rounded cursor-pointer w-16"
                            onClick={() => setEditingYear({ artworkId: artwork.id, value: artwork.year?.toString() || '' })}
                          >
                            {artwork.year || '-'}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingStatus.artworkId === artwork.id ? (
                          <div className="relative inline-block">
                            <select
                              value={editingStatus.value || artwork.status}
                              onChange={(e) => {
                                const value = e.target.value as 'draft' | 'published';
                                updateArtworkStatus(artwork.id, value);
                              }}
                              className="bg-gray-800 border border-gray-700 text-white text-xs rounded-md px-2 py-1 pr-6 appearance-none focus:outline-none focus:ring-1 focus:ring-yellow-500"
                              autoFocus
                              onBlur={() => setEditingStatus({ artworkId: null })}
                            >
                              <option value="draft">Brouillon</option>
                              <option value="published">Publié</option>
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                          </div>
                        ) : (
                          <div 
                            onClick={() => setEditingStatus({ artworkId: artwork.id, value: artwork.status as 'draft' | 'published' })}
                            className="inline-block cursor-pointer"
                          >
                            <Badge 
                              variant={artwork.status === 'published' ? 'default' : 'secondary'}
                              className={`text-xs ${
                                artwork.status === 'published' 
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30' 
                                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30 hover:bg-gray-500/30'
                              }`}
                            >
                              {artwork.status === 'published' ? 'Publié' : 'Brouillon'}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {artwork.created_at && format(new Date(artwork.created_at), 'dd MMM yyyy', { locale: fr })}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          {artwork.status === 'published' && (
                            <a 
                              href={`/portfolio/${artwork.slug}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                                <Eye size={18} />
                              </Button>
                            </a>
                          )}
                          <Link to={`/admin/portfolio/edit/${artwork.id}`}>
                            <Button variant="ghost" size="sm" className="text-yellow-400 hover:text-yellow-300">
                              <Edit size={18} />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(artwork.id)}
                            className="text-green-400 hover:text-green-300"
                          >
                            <Copy size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(artwork)}
                            className="text-[#D4AF37] hover:text-[#D4AF37]/70 transition-colors duration-200"
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <FileText className="h-12 w-12 text-gray-500 mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">Aucun projet trouvé</h3>
              <p className="text-gray-400">
                {searchQuery || selectedCategory || viewMode !== 'all' 
                  ? 'Aucun projet ne correspond à vos critères de recherche.' 
                  : 'Commencez par ajouter un nouveau projet à votre portfolio.'}
              </p>
              {(searchQuery || selectedCategory || viewMode !== 'all') && (
                <Button 
                  onClick={() => {
                    setSearchQuery('');
                    setViewMode('all');
                    setSelectedCategory('');
                  }}
                  className="mt-4 border-white/10 text-white hover:bg-white/5"
                  variant="outline"
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Dialogue de confirmation de suppression */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">Supprimer le projet</DialogTitle>
              <DialogDescription className="text-gray-400">
                Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>

            {artworkToDelete && (
              <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-blue-400 mr-2" />
                  <span className="font-medium text-white">{artworkToDelete.title}</span>
                </div>
                {artworkToDelete.description && (
                  <p className="mt-2 text-sm text-gray-400 line-clamp-2">{artworkToDelete.description}</p>
                )}
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="border-white/10 text-white hover:bg-white/5"
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deletingArtwork}
                className="bg-gradient-to-r from-[#002366] to-[#002366]/80 hover:from-[#002366]/90 hover:to-[#002366]/70 border border-[#D4AF37]/30 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {deletingArtwork ? (
                  <>
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Supprimer
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ArtworkList;
